import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TradingContext } from '../contexts/TradingContext';
import { AuthContext } from '../contexts/AuthContext';
import { useHistoricalOrdersInfiniteQuery } from './useTradeStationQueries';
import { orderMatchesSelectedAccount } from '../services/tradestationUtils';
import { sealTerminalOrder } from '../utils/orderReplace';

// One table page per request. Older pages load only when the user goes to the next page.
const HISTORICAL_ORDERS_PAGE_SIZE = 50;

/**
 * Custom hook to manage both historical and current orders.
 * Loads the first broker page only. Older pages load when the orders table asks
 * for the next cursor. Neither Connect Trade nor TradeStation returns a total count.
 */
export const useOrdersManager = () => {
    const { user } = useContext(AuthContext);
    const { isPaperTrading, selectedAccount, orders, setOrders } = useContext(TradingContext);

    const [historicalOrdersLoaded, setHistoricalOrdersLoaded] = useState(false);
    const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
    const [historicalOrdersError, setHistoricalOrdersError] = useState(null);

    /**
     * Merges historical orders with current orders, removing duplicates by OrderID
     * @param {Array} currentOrders - Current orders from stream/existing state
     * @param {Array} historicalOrders - Historical orders from API
     * @returns {Array} Merged orders without duplicates
     */
    const mergeOrders = useCallback((currentOrders, historicalOrders) => {
        if (!historicalOrders || historicalOrders.length === 0) {
            return currentOrders;
        }

        // Create a map of existing order IDs for fast lookup (always stringify — CT/TS mix types)
        const existingOrderIds = new Set(currentOrders.map(order => String(order.OrderID)));
        
        // Filter out historical orders that already exist in current orders
        const newHistoricalOrders = historicalOrders.filter(order => {
            if (order?.OrderID == null || existingOrderIds.has(String(order.OrderID))) return false;
            const sealed = sealTerminalOrder(order);
            if (sealed !== order) Object.assign(order, sealed);
            return orderMatchesSelectedAccount(order, selectedAccount);
        });

        // Merge and sort by OpenedDateTime (newest first)
        const mergedOrders = [...currentOrders, ...newHistoricalOrders];
        return mergedOrders.sort((a, b) => 
            new Date(b.OpenedDateTime || 0) - new Date(a.OpenedDateTime || 0)
        );
    }, [selectedAccount]);

    // Snapshot the query end boundary once on mount. The infinite query reuses this
    // end_time on every cursor page so the window does not slide forward mid-fetch.
    // The start of the window is computed server-side per broker.
    const endDate = useMemo(() => new Date().toISOString(), []);

    const historicalQuery = useHistoricalOrdersInfiniteQuery(
        isPaperTrading,
        selectedAccount,
        HISTORICAL_ORDERS_PAGE_SIZE,
        endDate
    );

    // Reflect query loading/error state
    useEffect(() => {
        setIsLoadingHistorical(
            historicalQuery.isLoading ||
            historicalQuery.isFetching ||
            historicalQuery.isFetchingNextPage
        );
        setHistoricalOrdersError(historicalQuery.error?.message || null);
    }, [
        historicalQuery.isLoading,
        historicalQuery.isFetching,
        historicalQuery.isFetchingNextPage,
        historicalQuery.error
    ]);

    // Merge each fetched page into orders; mark ready after the first page completes.
    useEffect(() => {
        if (!user?.token || !selectedAccount?.AccountID) return;
        const pages = historicalQuery.data?.pages;
        if (!pages?.length) return;

        const allHistorical = pages.flatMap((p) => p?.Orders || []);
        if (allHistorical.length > 0) {
            setOrders((current) => mergeOrders(current, allHistorical));
        }

        if (!historicalQuery.isFetching && !historicalQuery.isLoading) {
            setHistoricalOrdersLoaded(true);
        }
    }, [
        user?.token,
        selectedAccount?.AccountID,
        historicalQuery.data,
        historicalQuery.isFetching,
        historicalQuery.isLoading,
        setOrders,
        mergeOrders,
    ]);

    /**
     * Resets historical orders loading state when account changes
     */
    useEffect(() => {
        setHistoricalOrdersLoaded(false);
        setHistoricalOrdersError(null);
    }, [selectedAccount?.AccountID]);

    /**
     * Exposed manual fetch to advance pagination if needed
     */
    const fetchHistoricalOrders = useCallback(async () => {
        if (historicalQuery.hasNextPage && !historicalQuery.isFetchingNextPage) {
            await historicalQuery.fetchNextPage();
        }
    }, [historicalQuery.hasNextPage, historicalQuery.isFetchingNextPage, historicalQuery.fetchNextPage]);

    return {
        fetchHistoricalOrders,
        isLoadingHistorical,
        historicalOrdersLoaded,
        historicalOrdersError,
        hasMoreHistorical: !!historicalQuery.hasNextPage,
        isFetchingMoreHistorical: !!historicalQuery.isFetchingNextPage,
        mergeOrders,
    };
};